import { cloneDeep, get } from 'lodash'

/**
 * Работа со значением выпадающего списка: нормализация значения,
 * добавление и удаление выбранных значений, признаки блокировки и
 * возможности очистки.
 */
export const proxy = {
  props: {
    /**
     * Значение поля. Одиночное значение или массив значений при
     * multiple.
     *
     * @type {*}
     */
    value: null,

    /**
     * Значение, которое отдается наружу при полной очистке выбора
     * без multiple.
     *
     * @type {*}
     */
    defaultValue: null,

    /**
     * Признак множественного выбора. Выбранные значения отдаются
     * наружу массивом, а у пунктов списка выводятся отметки выбора.
     *
     * @type {boolean}
     * @default false
     */
    multiple: {
      type: Boolean,
      default: false
    },

    /**
     * Признак блокировки поля. Заблокированное поле не принимает
     * нажатия и не выводит поле ввода и выпадающее меню.
     *
     * @type {boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Признак поля только для чтения. Действует так же, как
     * disabled.
     *
     * @type {boolean}
     * @default false
     */
    readonly: {
      type: Boolean,
      default: false
    },
    /**
     * Признак вывода значка очистки. Значок выводится, только
     * когда поле не заблокировано и выбор не пуст.
     *
     * @type {boolean}
     * @default false
     */
    clearable: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    proxy: {
      /**
       * Отдает нормализованный список выбранных значений.
       * Значение поля приводится к массиву, из которого исключаются
       * undefined, null и пустые строки.
       *
       * @returns {Array<*>} Массив непустых выбранных значений.
       */
      get() {
        let value = [ this.value ]
        if (Array.isArray(this.value)) {
          value = this.value
        }
        const result = value.filter(item => {
          return item !== undefined && item !== null && item !== ''
        })

        return result
      },

      /**
       * Синхронизирует переданные значения с текущим выбором и
       * сообщает итог наружу событием input.
       *
       * Если isDisabled или переданное значение не массив, ничего
       * не происходит.
       *
       * Для непустого массива каждое значение обрабатывается по
       * очереди: undefined, null и пустые строки пропускаются; если
       * значения еще нет в выборе, при multiple оно добавляется в
       * конец, иначе заменяет весь выбор; если значение уже есть в
       * выборе, при multiple оно удаляется, кроме случая, когда
       * required и в выборе остается одно значение.
       *
       * Для пустого массива строка поиска очищается, если она есть,
       * а выбор очищается полностью, если нет required.
       *
       * При multiple наружу отдается весь список значений. Иначе
       * отдается единственное оставшееся значение, а при пустом
       * выборе - значение defaultValue.
       *
       * @param {Array<*>} value Значения для синхронизации с выбором.
       */
      set(value) {
        if (!this.isDisabled) {
          if (Array.isArray(value)) {
            const result = cloneDeep(this.proxy)
            if (value.length) {
              for (const item of value) {
                if (item === undefined || item === null || item === '') {
                  continue
                }

                const index = result.findIndex(v => {
                  return this._compareValues(v, item)
                })

                if (index === -1) {
                  if (this.multiple) {
                    result.push(item)
                  } else {
                    result.splice(0, result.length, item)
                  }
                } else {
                  if (this.multiple) {
                    if (!this.required || result.length > 1) {
                      result.splice(index, 1)
                    }
                  }
                }
              }
            } else {
              if (this.search) {
                this._clearSearchValue()
              }

              if (!this.required) {
                result.splice(0, result.length)
              }
            }

            if (this.multiple) {
              this.$emit('input', result)
            } else if (result.length) {
              this.$emit('input', result[0])
            } else {
              this.$emit('input', this.defaultValue)
            }
          }
        }
      }
    },

    /**
     * Отдает выбранные значения без undefined, null и пустых строк.
     *
     * @returns {Array<*>} Непустые выбранные значения.
     */
    filteredProxy() {
      return this.proxy.filter(value => {
        return value !== undefined && value !== null && value !== ''
      })
    },
    /**
     * Отдает признак пустого выбора.
     *
     * @returns {boolean} Признак отсутствия выбранных значений.
     */
    proxyIsEmpty() {
      return this.filteredProxy.length === 0
    },

    /**
     * Отдает признак недоступности поля.
     * Поле недоступно, если disabled или readonly.
     *
     * @returns {boolean} Признак недоступности поля.
     */
    isDisabled() {
      return this.disabled || this.readonly
    },
    /**
     * Отдает признак вывода значка очистки.
     * Значок выводится, если не isDisabled и clearable и не
     * proxyIsEmpty.
     *
     * @returns {boolean} Признак вывода значка очистки.
     */
    isClearable() {
      return !this.isDisabled && this.clearable && !this.proxyIsEmpty
    }
  },

  methods: {
    /**
     * Добавляет значение к выбранным.
     * Если isDisabled, ничего не происходит и отдается false. Если
     * значение уже выбрано, отдается false. Иначе значение
     * передается в выбор, а при отсутствии multiple строка поиска
     * очищается, список закрывается и фокус снимается; размеры меню
     * пересчитываются и отдается true.
     *
     * @param {object|*} value Элемент или его значение.
     * @returns {boolean} Признак того, что значение было добавлено.
     */
    addByValue(value) {
      if (this.isDisabled) {
        return false
      }

      value = get(value, this.$_itemValue, value)

      const index = this.proxy.findIndex(v => {
        return this._compareValues(v, value)
      })

      if (index === -1) {
        if (this.multiple) {
          this.proxy = [ value ]
        } else {
          this.proxy = [ value ]

          if (this.search) {
            this._clearSearchValue()
          }

          this._data._showDropdown = false
          this._data._focused = false
        }

        this._updateMenu()

        return true
      }

      return false
    },
    /**
     * Удаляет значение из выбранных.
     * Если isDisabled, ничего не происходит и отдается false. Если
     * значение не выбрано, отдается false. Если выбрано одно
     * значение и required, оно не удаляется и отдается false. Иначе
     * при multiple значение передается в выбор для снятия, а без
     * multiple выбор очищается; размеры меню пересчитываются и
     * отдается true.
     *
     * @param {object|*} value Элемент или его значение.
     * @returns {boolean} Признак того, что значение было удалено.
     */
    removeByValue(value) {
      if (this.isDisabled) {
        return false
      }

      value = get(value, this.$_itemValue, value)

      const index = this.proxy.findIndex(v => {
        return this._compareValues(v, value)
      })

      if (index > -1) {
        if (this.proxy.length === 1 && this.required) {
          return false
        }

        if (this.multiple) {
          this.proxy = [ value ]
        } else {
          this.proxy = []
        }
        this._updateMenu()

        return true
      }

      return false
    },
    /**
     * Добавляет значение к выбранным, а если оно уже выбрано -
     * удаляет его.
     *
     * @param {object|*} value Элемент или его значение.
     * @returns {boolean} Признак того, что выбор изменился.
     */
    toggleByValue(value) {
      return this.addByValue(value) || this.removeByValue(value)
    },

    /**
     * Записывает значение в выбор.
     * Если значение пустое, ничего не происходит. Если multiple,
     * значение переключается, иначе добавляется.
     *
     * @param {*} value Значение для записи.
     */
    _setValue(value) {
      if (value) {
        if (this.multiple) {
          this.toggleByValue(value)
        } else {
          this.addByValue(value)
        }
      }
    }
  }
}
